import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  LayoutDashboard, FileText, Calculator, Upload, Clock, CheckSquare,
  AlertTriangle, BookOpen, Bot, FolderOpen, Calendar, BarChart3,
  ChevronLeft, ChevronRight, Menu, X, ArrowLeft, Activity, Receipt, ScrollText, Hash
} from 'lucide-react';
import ISYDashboard from './isy/ISYDashboard';
import ISYIhaleler from './isy/ISYIhaleler';
import ISYMukayeseliKesif from './isy/ISYMukayeseliKesif';
import ISYSureTakip from './isy/ISYSureTakip';
import ISYGenelSartname from './isy/ISYGenelSartname';
import ISYEksiklikTakibi from './isy/ISYEksiklikTakibi';
import ISYMevzuat from './isy/ISYMevzuat';
import ISYAsistan from './isy/ISYAsistan';
import ISYOskaAmp from './isy/ISYOskaAmp';
import ISYBelgeler from './isy/ISYBelgeler';
import ISYTakvim from './isy/ISYTakvim';
import ISYRaporlar from './isy/ISYRaporlar';
import ISYKontrolPaneli from './isy/ISYKontrolPaneli';
import ISYHakedis from './isy/ISYHakedis';
import ISYIdariSartname from './isy/ISYIdariSartname';
import ISYBirimFiyat from './isy/ISYBirimFiyat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Ana Panel', icon: LayoutDashboard, active: true },
  { id: 'ihaleler', label: 'İhaleler & Sözleşmeler', icon: FileText, active: true },
  { id: 'kontrol_paneli', label: 'Kontrol Paneli', icon: Activity, active: true },
  { id: 'divider1', type: 'divider' },
  { id: 'oska_amp', label: 'OSKA/AMP Veri', icon: Upload, active: true },
  { id: 'idari_sartname', label: 'İdari Şartname / Sözleşme', icon: ScrollText, active: true, parent: 'oska_amp' },
  { id: 'birim_fiyat', label: 'Birim Fiyat / Pursantaj', icon: Hash, active: true, parent: 'oska_amp' },
  { id: 'mukayeseli_kesif', label: 'Mukayeseli Keşif', icon: Calculator, active: true, parent: 'oska_amp' },
  { id: 'hakedis', label: 'Hakediş', icon: Receipt, active: true, parent: 'oska_amp' },
  { id: 'sure_takip', label: 'Süre & Süreç Takibi', icon: Clock, active: true },
  { id: 'genel_sartname', label: 'Genel Şartname Uyum', icon: CheckSquare, active: true },
  { id: 'eksiklik', label: 'Eksiklik Takibi', icon: AlertTriangle, active: true },
  { id: 'divider2', type: 'divider' },
  { id: 'mevzuat', label: 'Mevzuat Değişiklikleri', icon: BookOpen, active: true },
  { id: 'asistan', label: 'Uzman Asistan', icon: Bot, active: true },
  { id: 'belgeler', label: 'Belgeler', icon: FolderOpen, active: true },
  { id: 'takvim', label: 'Takvim', icon: Calendar, active: true },
  { id: 'raporlar', label: 'Raporlar', icon: BarChart3, active: true },
];

export default function ISYPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [ihaleler, setIhaleler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const fetchIhaleler = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/isy/ihaleler`, { withCredentials: true });
      setIhaleler(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users-simple`, { withCredentials: true });
      setUsers(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchIhaleler(); fetchUsers(); }, [fetchIhaleler, fetchUsers]);

  const handleSeedData = async () => {
    try {
      const res = await axios.post(`${API}/isy/seed`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchIhaleler();
    } catch (e) { toast.error('Seed hatası'); }
  };

  const handleModuleClick = (id) => {
    const item = SIDEBAR_ITEMS.find(s => s.id === id);
    if (item && !item.active) {
      toast.info(`${item.label} modülü yakında eklenecek`);
      return;
    }
    setActiveModule(id);
    setMobileSidebarOpen(false);
  };

  const renderContent = () => {
    switch (activeModule) {
      case 'dashboard':
        return <ISYDashboard ihaleler={ihaleler} loading={loading} onSeedData={handleSeedData} onNavigate={handleModuleClick} />;
      case 'ihaleler':
        return <ISYIhaleler ihaleler={ihaleler} onRefresh={fetchIhaleler} isAdmin={user?.is_admin} users={users} />;
      case 'kontrol_paneli':
        return <ISYKontrolPaneli ihaleler={ihaleler} onNavigate={setActiveModule} />;
      case 'mukayeseli_kesif':
        return <ISYMukayeseliKesif ihaleler={ihaleler} onBack={() => setActiveModule('dashboard')} />;
      case 'sure_takip':
        return <ISYSureTakip ihaleler={ihaleler} onBack={() => setActiveModule('dashboard')} />;
      case 'genel_sartname':
        return <ISYGenelSartname ihaleler={ihaleler} onBack={() => setActiveModule('dashboard')} />;
      case 'eksiklik':
        return <ISYEksiklikTakibi ihaleler={ihaleler} onBack={() => setActiveModule('dashboard')} />;
      case 'oska_amp':
        return <ISYOskaAmp ihaleler={ihaleler} onNavigate={(id) => setActiveModule(id)} />;
      case 'hakedis':
        return <ISYHakedis ihaleler={ihaleler} onBack={() => setActiveModule('oska_amp')} />;
      case 'idari_sartname':
        return <ISYIdariSartname ihaleler={ihaleler} onBack={() => setActiveModule('oska_amp')} />;
      case 'birim_fiyat':
        return <ISYBirimFiyat ihaleler={ihaleler} onBack={() => setActiveModule('oska_amp')} />;
      case 'mevzuat':
        return <ISYMevzuat />;
      case 'asistan':
        return <ISYAsistan ihaleler={ihaleler} />;
      case 'belgeler':
        return <ISYBelgeler ihaleler={ihaleler} />;
      case 'takvim':
        return <ISYTakvim ihaleler={ihaleler} />;
      case 'raporlar':
        return <ISYRaporlar ihaleler={ihaleler} />;
      default:
        return (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Clock className="w-12 h-12 mb-3 text-gray-600" />
            <p className="text-lg font-medium text-gray-400">Yakında</p>
            <p className="text-sm">Bu modül henüz geliştirme aşamasında</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col" data-testid="isy-page">
      {/* Top Bar */}
      <header className="bg-gray-900/95 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} className="lg:hidden p-1.5 text-gray-400 hover:text-white rounded-md hover:bg-gray-800">
              {mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white h-8 px-2" data-testid="isy-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> İSY
            </Button>
            <div className="h-5 w-px bg-gray-700" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[#1e3a5f] flex items-center justify-center">
                <span className="text-[10px] font-bold text-[#c9a227]">ISY</span>
              </div>
              <div>
                <h1 className="text-sm font-bold text-white leading-tight">Yapım İhaleleri & Sözleşme Yönetimi</h1>
                <p className="text-[10px] text-gray-500 leading-tight">Akıllı Denetim Platformu</p>
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 bg-gray-800/50 rounded-lg px-2.5 py-1">
            <div className="w-6 h-6 rounded-full bg-[#1e3a5f] flex items-center justify-center">
              <span className="text-[10px] text-[#c9a227] font-bold">{user?.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <span className="text-xs text-white">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside className={`hidden lg:flex flex-col bg-gray-900/80 border-r border-gray-800 transition-all duration-200 ${sidebarCollapsed ? 'w-14' : 'w-56'}`}>
          <div className="flex items-center justify-end p-2">
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 text-gray-500 hover:text-gray-300 rounded">
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5">
            {SIDEBAR_ITEMS.map((item) => {
              if (item.type === 'divider') return <div key={item.id} className="h-px bg-gray-800 my-2" />;
              const Icon = item.icon;
              const isActive = activeModule === item.id;
              const isChild = !!item.parent;
              const isParentActive = isChild && (activeModule === item.parent || activeModule === item.id);
              return (
                <button key={item.id} onClick={() => handleModuleClick(item.id)} data-testid={`isy-nav-${item.id}`}
                  className={`w-full flex items-center gap-2.5 ${isChild ? 'pl-7 pr-2.5' : 'px-2.5'} py-2 rounded-md text-left transition-colors ${
                    isActive ? 'bg-[#1e3a5f]/40 text-[#c9a227] border-l-2 border-[#c9a227]' :
                    item.active ? 'text-gray-300 hover:bg-gray-800/60 hover:text-white' :
                    'text-gray-600 hover:bg-gray-800/30'
                  }`}>
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-[#c9a227]' : item.active ? 'text-gray-400' : 'text-gray-700'}`} />
                  {!sidebarCollapsed && <span className={`text-xs truncate ${isChild ? 'text-[11px]' : ''}`}>{item.label}</span>}
                  {!sidebarCollapsed && !item.active && <span className="text-[9px] text-gray-700 ml-auto">Yakında</span>}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Sidebar - Mobile Overlay */}
        {mobileSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-gray-900 border-r border-gray-800 overflow-y-auto pt-16">
              <nav className="px-3 pb-4 space-y-0.5">
                {SIDEBAR_ITEMS.map((item) => {
                  if (item.type === 'divider') return <div key={item.id} className="h-px bg-gray-800 my-2" />;
                  const Icon = item.icon;
                  const isActive = activeModule === item.id;
                  const isChild = !!item.parent;
                  return (
                    <button key={item.id} onClick={() => handleModuleClick(item.id)}
                      className={`w-full flex items-center gap-3 ${isChild ? 'pl-8 pr-3' : 'px-3'} py-2.5 rounded-md text-left ${
                        isActive ? 'bg-[#1e3a5f]/40 text-[#c9a227]' :
                        item.active ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600'
                      }`}>
                      <Icon className="w-4 h-4" />
                      <span className={`${isChild ? 'text-xs' : 'text-sm'}`}>{item.label}</span>
                      {!item.active && <span className="text-[10px] text-gray-700 ml-auto">Yakında</span>}
                    </button>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}
